/**
 * ChatPopup Component - Context Chat Integration
 *
 * Displays an iframe-based chat widget with context data from the listing.
 *
 * Key Features:
 * 1. Sends context ONLY after receiving CHAT_WIDGET_READY signal
 * 2. Guards prevent duplicate sends (ref-based, no state re-renders)
 * 3. Hash-based deduplication ensures data changes are detected
 * 4. Single source of truth: context sent in message handler
 */

import React, { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import { useIntl } from '../../util/reactIntl';
import { formatMoney, convertMoneyToNumber } from '../../util/currency';
import { getCommissionAdjustedPrice } from '../../util/commission';
import { useChatNavigation } from './useChatNavigation';
import styles from './ChatPopup.module.css';

export const ChatPopup = ({
  isOpen = true,
  onClose,
  listingData = null,
  reviews = [],
  tenantId = null,
  userId = null,
  theme = 'light',
  primaryColor = '#000000',
  mode = 'context-chat',
  baseUrl = process.env.REACT_APP_CHAT_WIDGET_URL,
  title = 'GoPilot™ — Here to help',
  providerCommission = 0,
}) => {
  const history = useHistory();
  const intl = useIntl();

  // Track if context has been sent (prevents duplicate sends)
  const contextSentRef = useRef(false);

  // Track what data we've sent (for deduplication via hash comparison)
  const lastSentDataRef = useRef(null);

  // Setup chat navigation handler
  const { iframeRef } = useChatNavigation({
    chatOrigin: baseUrl,
    onNavigate: (url) => {
      console.log('[ChatPopup] Navigating to:', url);
      history.push(url);
    },
    onCloseChat: () => {
      console.log('[ChatPopup] Closing chat after navigation');
      onClose();
    },
    onNavigationEvent: (data) => {
      console.log('[ChatPopup] Navigation event:', data);
      // Optional: Add analytics tracking here
    }
  });

  // Handle escape key to close popup
  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Lock background scroll while the popup is open so the page behind it
  // can't be scrolled/interacted with while it's covered by the backdrop
  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  // Handle postMessage communication with iframe
  useEffect(() => {
    const handleMessage = (event) => {
      // When chat-mfe signals it's ready, send context immediately
      if (event.data.type === 'CHAT_WIDGET_READY') {
        console.log('[ChatPopup] Chat widget ready, preparing to send context data');

        // GUARD 1: Check if we've already sent context
        if (contextSentRef.current) {
          console.log('[ChatPopup] Context already sent, skipping duplicate send');
          return;
        }

        // Only send if we have iframe ref and data
        if (!iframeRef.current || !(listingData || tenantId)) {
          console.log('[ChatPopup] No data to send or iframe not ready');
          return;
        }

        // Extract provider/author ID
        const providerId = listingData?.author?.id?.uuid || null;

        // Transform listing data to match context chat widget expectations
        const transformedListing = listingData ? {
          title: listingData.attributes?.title,
          name: listingData.attributes?.title,
          description: listingData.attributes?.description,
          price: (() => {
            const commissionAdjustedPrice = getCommissionAdjustedPrice(listingData, providerCommission);
            return commissionAdjustedPrice
              ? {
                  formatted: formatMoney(intl, commissionAdjustedPrice),
                  amount: convertMoneyToNumber(commissionAdjustedPrice),
                  currency: commissionAdjustedPrice.currency,
                }
              : null;
          })(),
          category: listingData.attributes?.publicData?.category,
          features: listingData.attributes?.publicData?.amenities || [],
          publicData: listingData.attributes?.publicData,
          attributes: listingData.attributes,
          ...listingData
        } : null;

        // Transform reviews data for context
        const transformedReviews = reviews.map(review => ({
          id: review.id?.uuid,
          rating: review.attributes?.rating,
          content: review.attributes?.content,
          createdAt: review.attributes?.createdAt,
          author: {
            id: review.author?.id?.uuid,
            displayName: review.author?.attributes?.profile?.displayName,
            firstName: review.author?.attributes?.profile?.firstName,
            lastName: review.author?.attributes?.profile?.lastName
          }
        }));

        const contextData = {
          type: 'CONTEXT_DATA',
          listing: transformedListing,
          reviews: transformedReviews,
          providerId: providerId,
          tenantId: tenantId,
          theme: theme,
          primaryColor: primaryColor,
          mode: mode
        };

        // GUARD 2: Check if data actually changed (deep comparison)
        const dataHash = JSON.stringify(contextData);
        if (lastSentDataRef.current === dataHash) {
          console.log('[ChatPopup] Context data unchanged, skipping send');
          return;
        }

        // Send the message (only reaches here if both guards pass)
        console.log('[ChatPopup] Sending context data to chat-mfe');
        iframeRef.current.contentWindow.postMessage(contextData, '*');

        // Update guards to prevent future sends
        lastSentDataRef.current = dataHash;
        contextSentRef.current = true;
      }

      // Listen for acknowledgment from chat-mfe
      if (event.data.type === 'CONTEXT_RECEIVED') {
        console.log('[ChatPopup] Context successfully received by chat-mfe');
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [listingData, reviews, tenantId, theme, primaryColor, mode, intl, providerCommission]);

  // Construct iframe URL with query parameters
  const getIframeUrl = () => {
    const params = new URLSearchParams();

    if (tenantId) params.append('tenantId', tenantId);
    if (userId) params.append('userId', userId);
    if (theme) params.append('theme', theme);
    if (primaryColor) params.append('primaryColor', primaryColor);
    if (mode) params.append('mode', mode);

    return `${baseUrl}?${params.toString()}`;
  };

  const popup = (
    <div style={{ display: isOpen ? 'block' : 'none' }}>
      <div className={styles.backdrop} onClick={onClose} aria-hidden="true" />
      <div className={styles.popupContainer} role="dialog" aria-modal="true" aria-label={title}>
        <div className={styles.popupHeader}>
          <h3 className={styles.popupTitle}>{title}</h3>
          <button
            onClick={onClose}
            className={styles.closeButton}
            aria-label="Close chat"
            title="Close chat (Esc)"
          >
            ×
          </button>
        </div>
        <div className={styles.chatArea}>
          <iframe
            ref={iframeRef}
            src={getIframeUrl()}
            width="100%"
            height="100%"
            frameBorder="0"
            title="Context Chat"
            style={{
              border: 'none',
              borderRadius: '0 0 var(--popup-border-radius) var(--popup-border-radius)'
            }}
          />
        </div>
      </div>
    </div>
  );

  return typeof document !== 'undefined'
    ? ReactDOM.createPortal(popup, document.body)
    : popup;
};

ChatPopup.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  listingData: PropTypes.object,
  reviews: PropTypes.array,
  tenantId: PropTypes.string,
  userId: PropTypes.string,
  theme: PropTypes.oneOf(['light', 'dark']),
  primaryColor: PropTypes.string,
  mode: PropTypes.string,
  baseUrl: PropTypes.string,
  title: PropTypes.string,
  providerCommission: PropTypes.number,
};
